import { useEffect, useState } from 'react'
import { analyticsAPI, trackRecordAPI } from '../services/api'
import api from '../services/api'
import type { PlatformDistribution, SignalPerformance } from '../types'
import {
  LineChart,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import type { AnalyticsOverview, SignalTrackRecordSummary } from '../types'
import LoadingScreen from '../components/LoadingScreen'
import DirectionSplitPanel from '../components/trackRecord/DirectionSplitPanel'
import ResultsDisclaimer from '../components/ResultsDisclaimer'
// Named apart from recharts' Tooltip, which this page also uses.
import InfoTooltip from '../components/Tooltip'
import { ANALYTICS_GLOSSARY } from '../config/glossary'
import { useAnalyticsRealtime } from '../hooks/useAnalyticsRealtime'
import {
  MIN_WIN_RATE_SAMPLE,
  NO_CLOSED_SIGNALS,
  TREND_MIN_CLOSED,
  formatWinRate,
  hasWinRate,
} from '../utils/winRate'

type PhaseRateKey = 'phase1_win_rate' | 'phase2_win_rate' | 'phase3_win_rate'

// Drawn only once a trained Phase 2 model is serving (overview.phase2_model).
// Before that, Phase 2 is Phase 1 x 0.9 and Phase 3 is built on it, and the
// "Phase 1" series -- closes with a Phase 1 score >= 0.70 -- is nearly every
// signal (the fallback gate already needs Phase 1 >= 0.667), i.e. the overall
// win rate under a phase name. So until then the chart is the single Win
// Rate line, and Phase 1 comes back named for what it measures.
const PHASE_LINES: { key: PhaseRateKey; name: string; stroke: string }[] = [
  { key: 'phase1_win_rate', name: 'Phase 1 score ≥ 0.70', stroke: '#22c55e' },
  { key: 'phase2_win_rate', name: 'Phase 2', stroke: '#eab308' },
  { key: 'phase3_win_rate', name: 'Phase 3', stroke: '#a855f7' },
]

// signal_performance rows are UTC calendar days stamped at midnight UTC, so
// they are labelled in UTC; local time would show US viewers the day before.
const formatPerfDay = (value: string) =>
  new Date(value).toLocaleDateString(undefined, { timeZone: 'UTC' })

// Shown while the record (overview.closed_signals, the same
// record_signal_filter() count) is under TREND_MIN_CLOSED.
const TREND_GATED_NOTE = `Appears once the record has ${TREND_MIN_CLOSED}+ closed signals — smaller samples don't show a trend.`
const PAGE_GATED_NOTE = `Analytics will return once the record has ${TREND_MIN_CLOSED}+ closed signals. The Dashboard and Track Record carry the current figures.`

// P/L is not measured yet: no dollar figure exists until position sizing is
// decided. Shown as unavailable, never as a $0 that reads as break-even.
const PL_UNAVAILABLE = 'P/L unavailable'
const PL_UNAVAILABLE_NOTE = 'P/L is not measured yet. Win rate is the tracked result.'

const PLATFORM_ICONS: Record<string, string> = {
  twitter: '🐦',
  instagram: '📸',
  youtube: '▶️',
  tiktok: '🎵',
  blog: '📝',
}

// Display labels for the Platform Distribution card, keyed by the lowercase
// `platform` from /api/analytics/platform-distribution (the ContentType enum).
const PLATFORM_LABELS: Record<string, string> = {
  twitter: 'Twitter',
  instagram: 'Instagram',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
  blog: 'Blog',
}

const PLATFORM_DISPLAY_ORDER = ['Twitter', 'Instagram', 'YouTube', 'TikTok', 'LinkedIn', 'Blog']

// Fallback display label for any ContentType not present in PLATFORM_LABELS.
const capitalize = (value: string) =>
  value.length > 0 ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : value

interface TopSymbol {
  symbol: string
  timeframe: string
  total_signals: number
  winning_signals: number
  win_rate: number
  avg_confidence: number | null
  avg_pnl_pct: number | null
}

const Analytics = () => {
  const [performance, setPerformance] = useState<SignalPerformance[]>([])
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null)
  const [topSymbols, setTopSymbols] = useState<TopSymbol[]>([])
  const [platforms, setPlatforms] = useState<PlatformDistribution[]>([])
  // The long/short split is all-time (it is the track record's own summary,
  // gross), so it does not follow the day-range selector.
  const [signalSummary, setSignalSummary] = useState<SignalTrackRecordSummary | null>(null)
  useEffect(() => {
    let active = true
    Promise.resolve(trackRecordAPI.getSignalSummary?.())
      .then((data) => {
        if (active) setSignalSummary(data ?? null)
      })
      .catch((error) => console.error('Track record summary failed to load:', error))
    return () => {
      active = false
    }
  }, [])
  const [loading, setLoading] = useState(true)
  const [days, setDays] = useState(30)

  const { liveSnapshot, engagementEvents, isConnected, lastUpdated } =
    useAnalyticsRealtime()

  useEffect(() => {
    const loadData = async () => {
      try {
        const [data, overviewData] = await Promise.all([
          analyticsAPI.signalPerformance(days),
          analyticsAPI.overview(),
        ])
        setPerformance(data)
        setOverview(overviewData)
      } catch (error) {
        console.error('Error loading analytics:', error)
      } finally {
        setLoading(false)
      }
      try {
        const resp = await api.get<TopSymbol[]>('/api/analytics/signals/top-symbols', {
          params: { min_signals: 5, limit: 10 },
        })
        setTopSymbols(resp.data)
      } catch {
        // non-critical — top symbols table stays empty
      }
      try {
        setPlatforms(await analyticsAPI.platformDistribution())
      } catch {
        // non-critical — every platform reads zero posts
      }
    }
    loadData()
  }, [days])

  if (loading) {
    return <LoadingScreen />
  }

  // Summary stats come from /analytics/overview, which is scoped to this
  // creator and counts every status. The signal_performance rows behind the
  // charts below are a platform-wide daily snapshot of CLOSED signals only,
  // so summing them here reported closed-signal totals for the whole platform
  // under a tile labelled "Total Signals" on the creator's own dashboard.
  const closedSignals = overview?.closed_signals ?? 0
  const totalSignals = overview?.total_signals ?? 0
  const winRate = overview?.overall_win_rate ?? null
  const totalWinning = hasWinRate(winRate, closedSignals)
    ? Math.round((winRate / 100) * closedSignals)
    : 0
  const totalPL = overview?.total_profit_loss ?? null
  // A failed overview reads as 0 closes: the gate stays shut on no answer.
  const trendShown = closedSignals >= TREND_MIN_CLOSED

  // Oldest first for the charts. A sorted copy: reversing `performance` in
  // place during render flipped the state array on every re-render.
  const sortedPerf = [...performance].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  )
  // The chart plots a day's rates only from MIN_WIN_RATE_SAMPLE closes, like
  // every other rate in the app: a 1-close day would draw a 100% or 0% spike.
  // A null leaves a gap (connectNulls is off) and the tooltip says "—". Phase
  // rates have no count of their own, so the day's total gates them too.
  const chartPerf = sortedPerf.map((p) =>
    p.total_signals >= MIN_WIN_RATE_SAMPLE
      ? p
      : { ...p, win_rate: null, phase1_win_rate: null, phase2_win_rate: null, phase3_win_rate: null }
  )
  // Phase lines need a trained model serving (one global answer, not a
  // per-row flag: rows written before a fix can carry stale rates) AND a day
  // with a rate for the line. The legend lists only drawn lines.
  const modelTrained = overview?.phase2_model?.trained === true
  const phaseLines = modelTrained
    ? PHASE_LINES.filter(({ key }) => sortedPerf.some((p) => p[key] != null))
    : []
  // Cumulative P&L series (chronological running sum). Only drawn when every
  // day has a P/L: a null day is unmeasured, not $0.
  const plMeasured =
    sortedPerf.length > 0 && sortedPerf.every((p) => p.total_profit_loss != null)
  let running = 0
  const cumulativePLData = plMeasured
    ? sortedPerf.map((p) => {
        running += p.total_profit_loss as number
        return { date: p.date, cumulative_pl: parseFloat(running.toFixed(2)) }
      })
    : []

  // Regime win-rate averages
  const regimeRows = (['trending', 'ranging', 'volatile'] as const).map((regime) => {
    const key = `${regime}_win_rate` as keyof SignalPerformance
    const valid = performance.filter((p) => p[key] != null)
    // No rows with a rate for this regime means no rate, not 0%.
    const winRate = valid.length > 0
      ? parseFloat((valid.reduce((s, p) => s + (p[key] as number), 0) / valid.length).toFixed(2))
      : null
    return { regime: regime.charAt(0).toUpperCase() + regime.slice(1), win_rate: winRate }
  })

  return (
    <div className="space-y-6">
      {!trendShown && (
        <p className="text-sm text-gray-600" data-testid="analytics-gated-note">
          {PAGE_GATED_NOTE}
        </p>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold text-gray-900">Analytics</h1>
          {/* Live connection badge */}
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
              isConnected
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-500'
            }`}
          >
            <span
              className={`w-2 h-2 rounded-full ${
                isConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
              }`}
            />
            {isConnected ? <InfoTooltip content={ANALYTICS_GLOSSARY.live}>Live</InfoTooltip> : 'Offline'}
          </span>
        </div>
        {/* The range scopes only the daily trend views, so it goes with them. */}
        {trendShown && (
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        )}
      </div>

      {/* Live Metrics Cards */}
      {liveSnapshot && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
              Live — Today
            </h2>
            {lastUpdated && (
              <span className="text-xs text-gray-500">
                Updated {new Date(lastUpdated).toLocaleTimeString()}
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-5 border-l-4 border-green-400">
              <p className="text-xs text-gray-500 uppercase tracking-wide">
                Active Subscribers
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {liveSnapshot.active_subscribers.toLocaleString()}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-5 border-l-4 border-blue-400">
              <p className="text-xs text-gray-500 uppercase tracking-wide">
                Revenue Today
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                ${(liveSnapshot.revenue_today_cents / 100).toFixed(2)}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-5 border-l-4 border-purple-400">
              <p className="text-xs text-gray-500 uppercase tracking-wide">
                Signals Today
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {liveSnapshot.signals_today}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-5 border-l-4 border-yellow-400">
              <p className="text-xs text-gray-500 uppercase tracking-wide">
                Engagement Today
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {liveSnapshot.engagement_today.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">Total Signals</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{totalSignals}</p>
          <p className="text-sm text-gray-500 mt-1" data-testid="total-signals-breakdown">
            {`${overview?.active_signals ?? 0} open · ${closedSignals} closed`}
            {(overview?.cancelled_signals ?? 0) > 0 &&
              ` · ${overview?.cancelled_signals} cancelled not counted`}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">
            <InfoTooltip content={ANALYTICS_GLOSSARY.winningSignals}>Winning Signals (closed)</InfoTooltip>
          </p>
          <p className="text-3xl font-bold text-success-600 mt-2">{totalWinning}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">
            <InfoTooltip content={ANALYTICS_GLOSSARY.winRateClosed}>Win Rate (closed)</InfoTooltip>
          </p>
          <p className="text-3xl font-bold text-primary-600 mt-2">
            {formatWinRate(winRate, closedSignals)}
          </p>
          {!hasWinRate(winRate, closedSignals) && (
            <p className="text-sm text-gray-500 mt-1">{NO_CLOSED_SIGNALS}</p>
          )}
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">
            <InfoTooltip content={ANALYTICS_GLOSSARY.totalPL}>Total P/L</InfoTooltip>
          </p>
          {totalPL == null ? (
            <>
              <p className="text-3xl font-bold mt-2 text-gray-400">Unavailable</p>
              <p className="text-sm text-gray-500 mt-1">{PL_UNAVAILABLE_NOTE}</p>
            </>
          ) : (
            <p
              className={`text-3xl font-bold mt-2 ${
                totalPL >= 0 ? 'text-success-600' : 'text-danger-600'
              }`}
            >
              ${totalPL.toFixed(2)}
            </p>
          )}
          {totalPL != null && <p className="text-sm text-gray-500 mt-1">before fees</p>}
        </div>
      </div>

      <ResultsDisclaimer />

      {signalSummary && signalSummary.closed_signals > 0 && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <DirectionSplitPanel summary={signalSummary} tone="light" />
        </div>
      )}

      {/* Win Rate Chart */}
      {!trendShown ? (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-1">Win Rate Trend</h2>
          <p className="text-sm text-gray-500" data-testid="trend-gated-note">
            {TREND_GATED_NOTE}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-1">Win Rate Trend</h2>
          <p className="text-xs text-gray-500 mb-4" data-testid="win-rate-trend-note">
            Days with fewer than {MIN_WIN_RATE_SAMPLE} closes aren't plotted.
          </p>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartPerf}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={formatPerfDay}
              />
              <YAxis />
              <Tooltip
                labelFormatter={formatPerfDay}
                // Empty phases are null: no point is drawn and no rate shown.
                formatter={(value: any) => (value == null ? '—' : `${value.toFixed(1)}%`)}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="win_rate"
                stroke="#0ea5e9"
                name="Win Rate"
                strokeWidth={2}
              />
              {phaseLines.map(({ key, name, stroke }) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={stroke}
                  name={name}
                  strokeWidth={1}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Cumulative P/L */}
      {!plMeasured ? (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Cumulative P/L</h2>
          <p className="text-lg font-semibold text-gray-400">{PL_UNAVAILABLE}</p>
          <p className="text-sm text-gray-500 mt-1">{PL_UNAVAILABLE_NOTE}</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Cumulative P/L</h2>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={cumulativePLData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={formatPerfDay}
              />
              <YAxis tickFormatter={(v) => `$${Number(v).toFixed(0)}`} />
              <Tooltip
                labelFormatter={formatPerfDay}
                formatter={(v: unknown) => [`$${Number(v).toFixed(2)}`, 'Cumulative P/L']}
              />
              <Line
                type="monotone"
                dataKey="cumulative_pl"
                stroke="#10b981"
                strokeWidth={2}
                dot={false}
                name="Cumulative P/L"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Regime Win Rates */}
      {trendShown && regimeRows.some((r) => (r.win_rate ?? 0) > 0) && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Win Rate by Regime</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={regimeRows}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="regime" />
              <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
              <Tooltip formatter={(v: unknown) => [`${Number(v).toFixed(1)}%`, 'Win Rate']} />
              <Bar dataKey="win_rate" fill="#6366f1" radius={[4, 4, 0, 0]} name="Win Rate" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Top Performing Symbols */}
      {topSymbols.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <h2 className="text-xl font-bold text-gray-900 p-6 border-b">Top Performing Symbols</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Symbol</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timeframe</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Signals</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Win Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg P/L %</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {topSymbols.map((row, i) => (
                  <tr key={i}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.symbol}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.timeframe}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.total_signals}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">
                      <span className={row.win_rate >= 60 ? 'text-green-600' : row.win_rate >= 50 ? 'text-yellow-600' : 'text-red-600'}>
                        {formatWinRate(row.win_rate, row.total_signals)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">
                      {row.avg_pnl_pct != null ? (
                        <span className={row.avg_pnl_pct >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {row.avg_pnl_pct >= 0 ? '+' : ''}{row.avg_pnl_pct.toFixed(2)}%
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Platform Distribution */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Platform Distribution</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {PLATFORM_DISPLAY_ORDER.map((displayLabel) => {
            const match = platforms.find(
              (item) => (PLATFORM_LABELS[item.platform.toLowerCase()] || capitalize(item.platform)) === displayLabel
            )
            const postCount = match?.post_count ?? 0
            const avgEngagement = match?.avg_engagement_rate ?? 0

            return (
              <div
                key={displayLabel}
                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
                data-testid="platform-row"
              >
                <span className="font-medium text-gray-800">{displayLabel}</span>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-500 tabular-nums">{postCount} posts</span>
                  <span className="text-sm font-semibold text-green-600 tabular-nums">
                    {avgEngagement.toFixed(1)}%{' '}
                    <InfoTooltip content={ANALYTICS_GLOSSARY.engagement(avgEngagement)}>engagement</InfoTooltip>
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Recent Activity Feed */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Recent Activity</h2>
        {engagementEvents.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">
            {isConnected
              ? 'Waiting for live engagement events…'
              : 'Connect to see live activity.'}
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {engagementEvents.map((evt, idx) => (
              <li
                key={`${evt.publication_id}-${evt.timestamp ?? idx}`}
                className="py-3 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl" aria-label={evt.platform}>
                    {PLATFORM_ICONS[evt.platform] ?? '📊'}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-gray-800 capitalize">
                      {evt.platform} · Publication #{evt.publication_id}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      👍 {evt.likes.toLocaleString()} &nbsp;
                      👁 {evt.views.toLocaleString()} &nbsp;
                      🔁 {evt.shares.toLocaleString()} &nbsp;
                      💬 {evt.comments.toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  {evt.delta && (
                    <div className="flex gap-2 text-xs font-semibold mb-1">
                      {(evt.delta.likes ?? 0) !== 0 && (
                        <span
                          className={
                            (evt.delta.likes ?? 0) > 0
                              ? 'text-green-600'
                              : 'text-red-500'
                          }
                        >
                          {(evt.delta.likes ?? 0) > 0 ? '+' : ''}
                          {evt.delta.likes} 👍
                        </span>
                      )}
                      {(evt.delta.views ?? 0) !== 0 && (
                        <span
                          className={
                            (evt.delta.views ?? 0) > 0
                              ? 'text-blue-600'
                              : 'text-red-500'
                          }
                        >
                          {(evt.delta.views ?? 0) > 0 ? '+' : ''}
                          {evt.delta.views} 👁
                        </span>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-gray-500">
                    {evt.timestamp
                      ? new Date(evt.timestamp).toLocaleTimeString()
                      : ''}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Performance Table: the trend's daily rows, gated with it */}
      {trendShown && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <h2 className="text-xl font-bold text-gray-900 p-6 border-b">
            Performance Details
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Signals
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Winning
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Win Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    P/L
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {performance.map((item) => (
                  <tr key={item.date}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatPerfDay(item.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.total_signals}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.winning_signals}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">
                      <span
                        className={
                          item.win_rate >= 70 ? 'text-success-600' : 'text-gray-900'
                        }
                      >
                        {formatWinRate(item.win_rate, item.total_signals)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">
                      {item.total_profit_loss == null ? (
                        <span className="text-gray-400" title={PL_UNAVAILABLE}>
                          —
                        </span>
                      ) : (
                        <span
                          className={
                            item.total_profit_loss >= 0
                              ? 'text-success-600'
                              : 'text-danger-600'
                          }
                        >
                          ${item.total_profit_loss.toFixed(2)}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default Analytics
